/* ==========================================================================
   Apus Ami — stylesheet, nạp vào bên trong shadow root.

   CÁCH LY
   Shadow DOM chặn mọi selector của app chủ, kể cả `!important`. Thứ duy nhất
   lọt qua là các thuộc tính kế thừa (font, color, line-height, letter-spacing…),
   nên `.apx-root` reset tay đúng nhóm đó. Không dùng `all: initial` vì nó xoá
   luôn custom property mà app chủ set trên host.

   BỐ CỤC
   Widget KHÔNG đụng vào layout của app chủ. Ở chế độ dock nó chiếm một dải cố
   định bên phải và báo bề rộng qua sự kiện `open`; app chủ tự thu hẹp nội dung
   chính của nó.
   ========================================================================== */

:host {
  --apx-foreground: #0f1115;
  --apx-ink2: #4b4e55;
  --apx-muted-ink: #8b909b;

  --apx-card: #ffffff;
  --apx-elev: #f6f7f9;
  --apx-fill: #eaecef;
  --apx-border: #dde1e8;

  /* bảng màu Apus Ami (apus-ami-widget/src/ami.css): xanh chim #1565d8 · trời #58b1e4 · vàng #d9b13b */
  --apx-brand: #1565d8;
  --apx-brand-foreground: #ffffff;
  --apx-primary: #1565d8;
  --apx-grad: linear-gradient(135deg, #1565d8, #58b1e4);
  --apx-accent: #d9b13b;
  --apx-navy: #0d1c3e;

  --apx-critical: #dc2626;
  --apx-critical-soft: rgba(220, 38, 38, 0.1);
  --apx-notice: #8f5d00;
  --apx-notice-soft: #fbf1d9;

  --apx-radius: 16px;
  --apx-header-h: 60px;
  --apx-width: 400px;
  --apx-font: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial,
    sans-serif;
  --apx-mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;

  /* Màu chuỗi biểu đồ: bảng phân loại đã kiểm định (dataviz), thứ tự cố định */
  --apx-series-1: #2a78d6;
  --apx-series-2: #eb6834;
  --apx-series-3: #1baf7a;
  --apx-series-4: #eda100;
  --apx-series-5: #e87ba4;
  --apx-series-6: #008300;

  display: block;
}

:host([data-theme="dark"]) {
  --apx-foreground: #f2f4f8;
  --apx-ink2: #aab0bc;
  --apx-muted-ink: #7c8391;

  --apx-card: #1e222b;
  --apx-elev: #272c36;
  --apx-fill: #272c36;
  --apx-border: #2f3540;

  --apx-brand: #4d8ff0;
  --apx-brand-foreground: #ffffff;
  --apx-primary: #4d8ff0;
  --apx-grad: linear-gradient(135deg, #1565d8, #58b1e4);
  --apx-accent: #e3c25a;
  --apx-navy: #0d1c3e;

  --apx-critical: #f87171;
  --apx-critical-soft: rgba(248, 113, 113, 0.14);
  --apx-notice: #d9a441;
  --apx-notice-soft: #2b2312;

  /* cùng 6 sắc, bước tối kiểm định trên nền tối */
  --apx-series-1: #3987e5;
  --apx-series-2: #d95926;
  --apx-series-3: #199e70;
  --apx-series-4: #c98500;
  --apx-series-5: #d55181;
  --apx-series-6: #008300;
}

:host([data-mode="dock"]),
:host([data-mode="float"]) {
  position: fixed;
  inset: 0;
  pointer-events: none;
}

:host([data-mode="inline"]) {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 420px;
}

/* --------------------------------------------------------------- reset */

.apx-root {
  /* Nhóm thuộc tính kế thừa — toàn bộ bề mặt mà CSS app chủ còn với tới được
     bên trong shadow root. */
  font-family: var(--apx-font);
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  font-variant: normal;
  line-height: 1.5;
  letter-spacing: normal;
  word-spacing: normal;
  text-align: left;
  text-transform: none;
  text-indent: 0;
  text-shadow: none;
  white-space: normal;
  color: var(--apx-foreground);
  direction: ltr;
  cursor: auto;
  visibility: visible;
  list-style: none;
  -webkit-font-smoothing: antialiased;
  -webkit-text-size-adjust: 100%;
}

.apx-root,
.apx-root *,
.apx-root *::before,
.apx-root *::after {
  box-sizing: border-box;
}

/* `:where()` đóng góp 0 specificity, nên reset này nằm dưới mọi rule class
   phía sau. Viết `.apx-root button` trực tiếp thì nó là (0,1,1) và sẽ đè chết
   `.apx-chip`, `.apx-launcher`… vốn chỉ (0,1,0). */
:where(.apx-root button, .apx-root textarea) {
  font: inherit;
  color: inherit;
  margin: 0;
  padding: 0;
  background: none;
  border: 0;
  text-align: inherit;
  appearance: none;
  -webkit-appearance: none;
}

/* ----------------------------------------------------------------- scrim */

/* Nền mờ chỉ tồn tại ở mobile, nơi panel chiếm toàn màn. Trên desktop panel
   dock nằm cạnh nội dung, chặn thao tác với app chủ là vô cớ. */
.apx-scrim {
  position: fixed;
  inset: 0;
  z-index: 1;
  display: none;
  background: rgba(4, 8, 20, 0.45);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

@media (max-width: 720px) {
  .apx-root[data-mode="dock"] .apx-scrim,
  .apx-root[data-mode="float"] .apx-scrim {
    display: block;
  }

  .apx-root[data-open="true"] .apx-scrim {
    opacity: 1;
    pointer-events: auto;
  }
}

/* ---------------------------------------------------------------- panel */

.apx-panel {
  position: fixed;
  z-index: 2;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  width: var(--apx-width);
  max-width: 100vw;
  background: var(--apx-card);
  border-left: 1px solid var(--apx-border);
  transition:
    transform 0.26s cubic-bezier(0.32, 0.72, 0, 1),
    opacity 0.2s ease;
}

/* -------- dock: dải cố định bên phải, dưới header của app chủ -------- */

.apx-root[data-mode="dock"] .apx-panel {
  top: var(--apx-header-h);
  bottom: 0;
  right: 0;
  box-shadow: -18px 0 42px -28px rgba(0, 0, 0, 0.45);
}

.apx-root[data-mode="dock"][data-anchor="left"] .apx-panel {
  right: auto;
  left: 0;
  border-left: 0;
  border-right: 1px solid var(--apx-border);
  box-shadow: 18px 0 42px -28px rgba(0, 0, 0, 0.45);
}

/* Đóng là trạng thái nghỉ — panel không bao giờ chớp hiện lúc tải trang. */
.apx-root[data-mode="dock"][data-open="false"] .apx-panel {
  transform: translateX(100%);
  opacity: 0;
  pointer-events: none;
}

.apx-root[data-mode="dock"][data-anchor="left"][data-open="false"] .apx-panel {
  transform: translateX(-100%);
}

/* -------- float: bong bóng nổi, cho các trang ngoài app -------- */

.apx-root[data-mode="float"] .apx-panel {
  top: auto;
  right: 20px;
  bottom: 88px;
  /* Hai dòng, không phải một. Trình duyệt chưa biết `dvh` bỏ qua dòng dưới và
     dùng `vh`; trình duyệt biết thì ghi đè. Không có dòng `vh` thì cả khai báo
     bị vứt và panel co lại theo nội dung. */
  height: min(600px, calc(100vh - 140px));
  height: min(600px, calc(100dvh - 140px));
  border: 1px solid var(--apx-border);
  border-radius: var(--apx-radius);
  overflow: hidden;
  box-shadow:
    0 24px 60px -20px rgba(0, 0, 0, 0.5),
    0 8px 24px rgba(0, 0, 0, 0.18);
}

.apx-root[data-mode="float"][data-anchor="left"] .apx-panel {
  right: auto;
  left: 20px;
}

.apx-root[data-mode="float"][data-open="false"] .apx-panel {
  transform: translateY(14px) scale(0.97);
  opacity: 0;
  pointer-events: none;
}

/* -------- inline: khối bình thường trong dòng chảy của app chủ -------- */

.apx-root[data-mode="inline"] {
  height: 100%;
}

.apx-root[data-mode="inline"] .apx-panel {
  position: relative;
  inset: auto;
  width: 100%;
  height: 100%;
  min-height: 420px;
  transform: none;
  opacity: 1;
  border: 0;
  box-shadow: none;
}

/* -------- mobile: toàn màn, đúng như mô tả trải nghiệm -------- */

@media (max-width: 720px) {
  .apx-root[data-mode="dock"] .apx-panel,
  .apx-root[data-mode="float"] .apx-panel {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    max-height: none;
    border: 0;
    border-radius: 0;
  }

  .apx-root[data-mode="float"][data-open="false"] .apx-panel,
  .apx-root[data-mode="dock"][data-open="false"] .apx-panel {
    transform: translateY(100%);
    opacity: 1;
  }
}

/* ------------------------------------------------------------------ head */

.apx-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
  padding: 14px 12px 12px 16px;
  border-bottom: 1px solid var(--apx-border);
}

.apx-avatar {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex: none;
  overflow: hidden;
  border-radius: 11px;
  color: var(--apx-brand-foreground);
  background: var(--apx-grad);
}

.apx-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.apx-headtext {
  min-width: 0;
  flex: 1;
}

.apx-title {
  margin: 0;
  font-size: 14.5px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--apx-foreground);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.apx-sub {
  margin: 2px 0 0;
  font-size: 11.5px;
  line-height: 1.35;
  color: var(--apx-muted-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.apx-headbtn {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  flex: none;
  border-radius: 9px;
  border: 1px solid transparent;
  color: var(--apx-muted-ink);
  cursor: pointer;
  transition:
    color 0.15s ease,
    background 0.15s ease;
}

.apx-headbtn:hover {
  color: var(--apx-foreground);
  background: var(--apx-fill);
}

/* Inline không có gì để đóng — panel chính là nội dung của trang. */
.apx-root[data-mode="inline"] .apx-close {
  display: none;
}

/* --------------------------------------------------------------- scroll */

.apx-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 14px 16px 4px;
  scrollbar-width: thin;
  scrollbar-color: var(--apx-border) transparent;
}

.apx-scroll::-webkit-scrollbar {
  width: 8px;
}

.apx-scroll::-webkit-scrollbar-thumb {
  background: var(--apx-border);
  border-radius: 99px;
}

/* ---------------------------------------------------------------- intro */

/* Tóm tắt mở đầu: dựng sẵn từ server, không gọi mô hình, không tính lượt. */
.apx-summary {
  padding: 13px 14px;
  border-radius: 13px;
  background: var(--apx-elev);
  border: 1px solid var(--apx-border);
  font-size: 13px;
  line-height: 1.55;
  color: var(--apx-ink2);
}

.apx-summary > :first-child { margin-top: 0; }
.apx-summary > :last-child { margin-bottom: 0; }

.apx-group {
  margin: 16px 2px 8px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--apx-muted-ink);
}

.apx-chips {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
}

.apx-chip {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--apx-ink2);
  background: var(--apx-card);
  border: 1px solid var(--apx-border);
  padding: 8px 13px;
  border-radius: 999px;
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.15s ease,
    color 0.15s ease;
}

.apx-chip:hover {
  border-color: var(--apx-primary);
  color: var(--apx-foreground);
}

/* --------------------------------------------------------------- thread */

.apx-thread {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 8px;
}

.apx-intro:not(:empty) + .apx-thread:not(:empty) {
  margin-top: 18px;
}

.apx-msg {
  max-width: 92%;
  font-size: 13px;
  line-height: 1.55;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.apx-msg-user {
  align-self: flex-end;
  max-width: 88%;
  padding: 9px 12px;
  border-radius: 14px 14px 4px 14px;
  color: var(--apx-brand-foreground);
  background: var(--apx-brand);
  white-space: pre-wrap;
}

.apx-msg-assistant {
  align-self: stretch;
  max-width: 100%;
  padding: 11px 13px;
  border-radius: 4px 14px 14px 14px;
  color: var(--apx-ink2);
  background: var(--apx-elev);
  border: 1px solid var(--apx-border);
}

.apx-msg-system {
  align-self: center;
  max-width: 100%;
  font-size: 11.5px;
  color: var(--apx-muted-ink);
  text-align: center;
}

.apx-msg-error {
  color: var(--apx-critical);
  background: var(--apx-critical-soft);
  border-color: var(--apx-critical);
}

.apx-body > :first-child { margin-top: 0; }
.apx-body > :last-child { margin-bottom: 0; }

/* Con trỏ nhấp nháy ở đuôi câu trả lời đang chảy về. */
.apx-msg-streaming .apx-body::after {
  content: "";
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 2px;
  vertical-align: text-bottom;
  background: currentColor;
  animation: apx-caret 1s steps(2) infinite;
}

@keyframes apx-caret {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

/* ------------------------------------------------------- nhãn số mẫu */

.apx-sample {
  display: inline-block;
  margin-top: 9px;
  padding: 2px 8px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.03em;
  border-radius: 99px;
  color: var(--apx-notice);
  background: var(--apx-notice-soft);
  border: 1px solid currentColor;
}

/* ----------------------------------------------------------- khối nguồn */

/* Ngay dưới câu trả lời, không giấu sau nút bấm: mọi con số phải đối chiếu
   được với widget hoặc báo cáo. */
.apx-sources {
  margin-top: 10px;
  padding-top: 9px;
  border-top: 1px solid var(--apx-border);
  display: grid;
  gap: 4px;
}

.apx-source {
  margin: 0;
  font-size: 11px;
  line-height: 1.45;
  color: var(--apx-muted-ink);
}

.apx-source-line {
  display: block;
}

.apx-source-note {
  display: block;
  font-style: italic;
  opacity: 0.85;
}

/* ------------------------------------------------------ chip bước tiếp theo */

.apx-actions {
  margin-top: 11px;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.apx-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 9px;
  cursor: pointer;
  color: var(--apx-primary);
  background: var(--apx-card);
  border: 1px solid var(--apx-border);
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}

.apx-action:hover {
  border-color: var(--apx-primary);
  background: var(--apx-fill);
}

.apx-action-ico {
  display: grid;
  place-items: center;
  opacity: 0.75;
}

.apx-retry {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 9px;
  padding: 5px 10px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--apx-critical);
  border: 1px solid currentColor;
  border-radius: 8px;
  cursor: pointer;
}

/* ----------------------------------------- markdown trong bong bóng */

.apx-msg .apx-p,
.apx-summary .apx-p {
  margin: 0 0 8px;
}

.apx-msg .apx-p:last-child,
.apx-summary .apx-p:last-child {
  margin-bottom: 0;
}

.apx-msg .apx-h {
  margin: 12px 0 6px;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--apx-foreground);
}

.apx-msg .apx-h:first-child {
  margin-top: 0;
}

.apx-msg .apx-list,
.apx-summary .apx-list {
  margin: 0 0 8px;
  padding-left: 20px;
  list-style: revert;
}

.apx-msg .apx-list:last-child {
  margin-bottom: 0;
}

.apx-msg .apx-list li {
  margin: 2px 0;
}

.apx-msg .apx-a,
.apx-summary .apx-a {
  color: var(--apx-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.apx-msg-user .apx-a {
  color: inherit;
}

.apx-msg .apx-code,
.apx-summary .apx-code {
  font-family: var(--apx-mono);
  font-size: 0.88em;
  padding: 1px 5px;
  border-radius: 5px;
  background: var(--apx-fill);
  border: 1px solid var(--apx-border);
}

.apx-msg .apx-pre {
  margin: 0 0 8px;
  padding: 10px 12px;
  overflow-x: auto;
  border-radius: 10px;
  background: var(--apx-fill);
  border: 1px solid var(--apx-border);
}

.apx-msg .apx-pre code {
  font-family: var(--apx-mono);
  font-size: 12px;
  line-height: 1.55;
  white-space: pre;
  background: none;
  border: 0;
  padding: 0;
}

.apx-msg .apx-quote {
  margin: 0 0 8px;
  padding: 2px 0 2px 10px;
  border-left: 3px solid var(--apx-border);
  color: var(--apx-muted-ink);
}

.apx-msg .apx-hr {
  margin: 10px 0;
  border: 0;
  border-top: 1px solid var(--apx-border);
}

/* ------------------------------------------------------- typing indicator */

.apx-typing {
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 9px;
  max-width: min(100%, 34ch);
  padding: 10px 13px;
  border-radius: 4px 14px 14px 14px;
  background: var(--apx-elev);
  border: 1px solid var(--apx-border);
}

.apx-typing-dots {
  display: inline-flex;
  flex: none;
  gap: 4px;
}

.apx-typing-dots > span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--apx-muted-ink);
  animation: apx-blink 1.2s ease-in-out infinite;
}

.apx-typing-dots > span:nth-child(2) { animation-delay: 0.18s; }
.apx-typing-dots > span:nth-child(3) { animation-delay: 0.36s; }

.apx-typing-label {
  color: var(--apx-ink2);
  font-size: 12.5px;
  line-height: 1.35;
  text-wrap: pretty;
}

@keyframes apx-blink {
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
}

/* ------------------------------------------------------------- composer */

.apx-composer {
  flex: none;
  padding: 10px 16px 12px;
  border-top: 1px solid var(--apx-border);
  background: var(--apx-card);
}

.apx-form {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 5px 5px 5px 14px;
  border-radius: 14px;
  border: 1px solid var(--apx-border);
  background: var(--apx-fill);
  transition: border-color 0.15s ease;
}

.apx-form:focus-within {
  border-color: var(--apx-primary);
}

.apx-form[hidden] {
  display: none;
}

.apx-input {
  flex: 1;
  min-width: 0;
  max-height: 120px;
  padding: 9px 0;
  border: 0;
  outline: none;
  resize: none;
  overflow-y: auto;
  background: transparent;
  font-family: inherit;
  /* 16px chặn iOS tự phóng to khi focus; pinch-zoom vẫn dùng được. */
  font-size: 16px;
  line-height: 1.4;
  color: var(--apx-foreground);
}

.apx-input::placeholder {
  color: var(--apx-muted-ink);
}

.apx-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (min-width: 721px) {
  .apx-input { font-size: 13.5px; }
}

.apx-act {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex: none;
  border-radius: 10px;
  cursor: pointer;
  transition:
    color 0.15s ease,
    background 0.15s ease,
    opacity 0.15s ease;
}

.apx-send {
  color: var(--apx-brand-foreground);
  background: var(--apx-brand);
}

.apx-send:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.apx-stop {
  color: var(--apx-brand-foreground);
  background: var(--apx-critical);
}

.apx-mic {
  border: 1px solid var(--apx-border);
  background: var(--apx-card);
  color: var(--apx-muted-ink);
}

.apx-mic:hover {
  color: var(--apx-foreground);
  border-color: var(--apx-primary);
}

.apx-mic[hidden],
.apx-act[hidden] {
  display: none;
}

.apx-mic.apx-recording {
  color: var(--apx-brand-foreground);
  border-color: transparent;
  background: var(--apx-critical);
  animation: apx-pulse 1.3s ease-in-out infinite;
}

@keyframes apx-pulse {
  0%, 100% { box-shadow: 0 0 0 0 var(--apx-critical-soft); }
  50% { box-shadow: 0 0 0 6px transparent; }
}

/* -------- dòng hạn mức + đếm ký tự -------- */

.apx-meterrow {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 7px;
  min-height: 15px;
}

.apx-meter {
  flex: 1;
  margin: 0;
  font-size: 11px;
  color: var(--apx-muted-ink);
}

.apx-meter[hidden] {
  display: none;
}

.apx-counter {
  font-family: var(--apx-mono);
  font-size: 10.5px;
  color: var(--apx-muted-ink);
  font-variant-numeric: tabular-nums;
}

.apx-counter[hidden] {
  display: none;
}

.apx-counter-full {
  color: var(--apx-critical);
  font-weight: 600;
}

/* Chạm trần hạn mức, hoặc trợ lý bị tắt từ xa. Panel vẫn mở để đọc lại. */
.apx-blocked {
  margin: 0;
  padding: 12px 14px;
  font-size: 12.5px;
  line-height: 1.5;
  text-align: center;
  color: var(--apx-notice);
  background: var(--apx-notice-soft);
  border: 1px solid currentColor;
  border-radius: 12px;
}

/* -------------------------------------------------------------- launcher */

/* Bong bóng góc phải dưới — mặc định BẬT ở Apus Ami (ERP chỉ dán script).
   App có nút hỏi riêng thì đặt launcher: false. */
.apx-launcher {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 3;
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  pointer-events: auto;
  border-radius: 50%;
  cursor: pointer;
  color: var(--apx-brand-foreground);
  background: var(--apx-grad);
  box-shadow:
    0 10px 28px -8px rgba(0, 0, 0, 0.45),
    0 2px 6px rgba(0, 0, 0, 0.18);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.apx-launcher[hidden] {
  display: none;
}

.apx-root[data-anchor="left"] .apx-launcher {
  right: auto;
  left: 20px;
}

.apx-launcher:hover {
  transform: translateY(-2px);
}

.apx-launcher svg + svg { display: none; }
.apx-root[data-open="true"] .apx-launcher svg:first-child { display: none; }
.apx-root[data-open="true"] .apx-launcher svg + svg { display: block; }

@media (max-width: 720px) {
  .apx-root[data-open="true"] .apx-launcher {
    opacity: 0;
    pointer-events: none;
  }
}

/* --------------------------------------------------------- a11y / motion */

.apx-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.apx-root button:focus-visible,
.apx-root [tabindex]:focus-visible {
  outline: 2px solid var(--apx-primary);
  outline-offset: 2px;
}

.apx-input:focus-visible {
  outline: none;
}

@media (prefers-reduced-motion: reduce) {
  .apx-panel,
  .apx-scrim,
  .apx-launcher,
  .apx-mic,
  .apx-typing-dots > span,
  .apx-msg-streaming .apx-body::after {
    transition-duration: 0.01ms;
    animation: none;
  }
}

/* Bảng trong câu trả lời (BOM, giá thành, tiêu chuẩn QC). Cuộn ngang trong
   bong bóng thay vì làm vỡ bề rộng panel. */
.apx-tw {
  max-width: 100%;
  overflow-x: auto;
  margin: 6px 0 8px;
  border: 1px solid var(--apx-border);
  border-radius: 8px;
}
.apx-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
}
.apx-table th,
.apx-table td {
  padding: 5px 8px;
  text-align: left;
  vertical-align: top;
  border-bottom: 1px solid var(--apx-border);
  white-space: nowrap;
}
.apx-table td:first-child,
.apx-table th:first-child {
  white-space: normal;
  min-width: 120px;
}
.apx-table th {
  font-weight: 600;
  color: var(--apx-muted-ink);
  background: var(--apx-elev);
}
.apx-table tr:last-child td {
  border-bottom: 0;
}
.apx-table .apx-right {
  text-align: right;
}
.apx-table .apx-center {
  text-align: center;
}

/* -------------------------------------------------------------- biểu đồ */
.apx-chart {
  margin: 8px 0 10px;
  padding: 10px 10px 6px;
  border: 1px solid var(--apx-border);
  border-radius: 10px;
  background: var(--apx-card);
}
.apx-chart svg {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}
.apx-chart-title {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--apx-foreground);
  margin: 0 0 6px;
}
.apx-chart-title span {
  font-weight: 400;
  color: var(--apx-muted-ink);
}
.apx-chart .apx-grid {
  stroke: var(--apx-border);
  stroke-width: 1;
}
.apx-chart .apx-axis {
  stroke: var(--apx-muted-ink);
  stroke-width: 1;
}
.apx-chart .apx-tick {
  font-size: 9.5px;
  fill: var(--apx-muted-ink);
}
.apx-chart .apx-val {
  font-size: 10px;
  font-weight: 600;
  fill: var(--apx-foreground);
}
.apx-chart .apx-dot {
  stroke: var(--apx-card);
  stroke-width: 2;
}
.apx-chart .apx-slice {
  stroke: var(--apx-card);
  stroke-width: 2;
}
.apx-chart path:hover,
.apx-chart rect:hover,
.apx-chart circle:hover {
  opacity: 0.8;
}
.apx-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin: 6px 0 2px;
  font-size: 11.5px;
  color: var(--apx-ink2);
}
.apx-legend span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.apx-legend i {
  width: 9px;
  height: 9px;
  border-radius: 2px;
  display: inline-block;
}
.apx-chart-note {
  margin: 4px 0 0;
  font-size: 11px;
  color: var(--apx-muted-ink);
}
.apx-chart-data {
  margin-top: 4px;
  font-size: 11.5px;
}
.apx-chart-data summary {
  cursor: pointer;
  color: var(--apx-ink2);
}
.apx-chart-pending {
  font-size: 12px;
  color: var(--apx-muted-ink);
  padding: 14px 10px;
  text-align: center;
}

/* ------------------------------------------------- bản xem trước HTML/SVG, ảnh */
.apx-html {
  margin: 8px 0 10px;
}
.apx-html-frame {
  display: block;
  width: 100%;
  height: 360px;
  border: 1px solid var(--apx-border);
  border-radius: 10px;
  background: #fff;
  color-scheme: light;
}
.apx-html-code {
  margin-top: 4px;
  font-size: 11.5px;
}
.apx-html-code summary {
  cursor: pointer;
  color: var(--apx-ink2);
}
.apx-html-code .apx-pre {
  margin-top: 6px;
  max-height: 320px;
  overflow: auto;
}
.apx-img {
  display: block;
  max-width: 100%;
  max-height: 420px;
  height: auto;
  margin: 6px 0;
  border-radius: 8px;
  border: 1px solid var(--apx-border);
}

/* ------------------------------------------------- kích thước panel (rộng / toàn màn hình) */
/* rộng: biểu đồ và bảng không phải cuộn ngang */
.apx-root[data-mode="float"][data-size="wide"] .apx-panel {
  width: min(760px, calc(100vw - 40px));
  height: min(880px, calc(100vh - 120px));
  height: min(880px, calc(100dvh - 120px));
}
/* toàn màn hình: nổi đè cả trang, có lớp nền mờ; nội dung giữa, dòng không quá dài */
.apx-root[data-size="full"] .apx-panel {
  top: 16px;
  right: 16px;
  bottom: 16px;
  left: 16px;
  width: auto;
  height: auto;
  max-width: none;
  z-index: 5;
  border: 1px solid var(--apx-border);
  border-radius: var(--apx-radius);
  box-shadow: 0 0 0 100vmax rgba(10, 12, 16, 0.38), 0 24px 60px -20px rgba(0, 0, 0, 0.5);
}
.apx-root[data-size="full"] .apx-thread,
.apx-root[data-size="full"] .apx-intro {
  max-width: 980px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
}
.apx-root[data-size="full"][data-open="true"] .apx-launcher {
  opacity: 0;
  pointer-events: none;
}
.apx-root[data-size="full"][data-open="false"] .apx-panel {
  box-shadow: none;
}
/* rộng thì biểu đồ không cần to hơn 640px (giữ tỷ lệ đọc được) */
.apx-root[data-size="full"] .apx-chart svg {
  max-width: 720px;
}
@media (max-width: 720px) {
  .apx-widen,
  .apx-fullbtn {
    display: none;
  }
}

/* =====================================================================
   Đề xuất hành động, thanh công cụ dưới câu trả lời (mọi cỡ)
   ===================================================================== */
:host {
  --apx-success: #15803d;
  --apx-success-soft: #e7f6ec;
  --apx-info: #1d4ed8;
  --apx-info-soft: #e8efff;
}
:host([data-theme="dark"]) {
  --apx-success: #4ade80;
  --apx-success-soft: rgba(74, 222, 128, 0.12);
  --apx-info: #93b4ff;
  --apx-info-soft: rgba(147, 180, 255, 0.12);
}

.apx-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 9px;
  border: 1px solid var(--apx-border);
  background: var(--apx-card);
  color: var(--apx-foreground);
  font: 600 12.5px/1 var(--apx-font);
  cursor: pointer;
  white-space: nowrap;
}
.apx-btn:hover { background: var(--apx-elev); }
.apx-btn:disabled { opacity: 0.55; cursor: default; }
.apx-btn:focus-visible { outline: 2px solid var(--apx-brand); outline-offset: 2px; }
.apx-btn-primary { background: var(--apx-brand); border-color: var(--apx-brand); color: var(--apx-brand-foreground); }
.apx-btn-primary:hover { background: var(--apx-brand); filter: brightness(1.08); }
.apx-btn-ok { background: var(--apx-success); border-color: var(--apx-success); color: #fff; }
.apx-btn-ok:hover { background: var(--apx-success); filter: brightness(1.08); }
.apx-btn-danger { color: var(--apx-critical); border-color: var(--apx-critical); }
.apx-btn-ghost { background: transparent; }
.apx-sm { min-height: 28px; padding: 0 10px; font-size: 12px; }
.apx-block { width: 100%; margin-top: 10px; }
.apx-danger { color: var(--apx-critical); }
.apx-link { border: 0; background: none; padding: 0; color: var(--apx-brand); font: 600 12px var(--apx-font); cursor: pointer; }

.apx-prop {
  margin-top: 10px;
  border: 1.5px solid var(--apx-border);
  border-radius: 12px;
  overflow: hidden;
  background: var(--apx-card);
}
.apx-prop-proposed, .apx-prop-need_info { border-color: var(--apx-notice); }
.apx-prop-head {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--apx-ink2);
  background: var(--apx-elev);
}
.apx-prop-proposed .apx-prop-head, .apx-prop-need_info .apx-prop-head { color: var(--apx-notice); background: var(--apx-notice-soft); }
.apx-prop-done .apx-prop-head { color: var(--apx-success); background: var(--apx-success-soft); }
.apx-prop-ico { display: inline-flex; }
.apx-prop-kind { flex: 1; min-width: 0; }
.apx-prop-demo { font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; padding: 1px 6px; border-radius: 99px; border: 1px solid currentColor; opacity: 0.8; }
.apx-prop-body { padding: 10px 12px 12px; }
.apx-prop-title { font-weight: 650; font-size: 14px; color: var(--apx-foreground); }
.apx-prop-meta { font-size: 11.5px; color: var(--apx-muted-ink); margin-top: 2px; }
.apx-prop-fields { margin: 9px 0 0; padding: 8px 10px; border-radius: 8px; background: var(--apx-elev); display: grid; gap: 4px; }
.apx-prop-row { display: grid; grid-template-columns: minmax(90px, 34%) 1fr; gap: 10px; font-size: 12.5px; }
.apx-prop-row dt { color: var(--apx-muted-ink); }
.apx-prop-row dd { margin: 0; color: var(--apx-foreground); font-weight: 500; }
.apx-prop-reason { margin: 8px 0 0; font-size: 12.5px; color: var(--apx-ink2); }
.apx-prop-warn, .apx-prop-policy, .apx-prop-note { display: flex; gap: 6px; align-items: flex-start; margin: 7px 0 0; padding: 7px 9px; border-radius: 8px; font-size: 12px; line-height: 1.45; }
.apx-prop-warn { color: var(--apx-notice); background: var(--apx-notice-soft); }
.apx-prop-policy { color: var(--apx-info); background: var(--apx-info-soft); }
.apx-prop-note { color: var(--apx-muted-ink); padding: 0; }
.apx-prop-warn svg, .apx-prop-policy svg { flex-shrink: 0; margin-top: 2px; }
.apx-prop-buttons { display: flex; justify-content: flex-end; gap: 8px; margin-top: 10px; flex-wrap: wrap; }
.apx-prop-status { margin-top: 10px; padding: 7px 10px; border-radius: 8px; font-size: 12.5px; font-weight: 600; }
.apx-tone-ok { color: var(--apx-success); background: var(--apx-success-soft); }
.apx-tone-bad { color: var(--apx-critical); background: var(--apx-critical-soft); }
.apx-tone-warn { color: var(--apx-notice); background: var(--apx-notice-soft); }
.apx-tone-info { color: var(--apx-info); background: var(--apx-info-soft); }
.apx-tone-muted { color: var(--apx-muted-ink); background: var(--apx-elev); }
.apx-prop-history { margin-top: 8px; font-size: 11.5px; color: var(--apx-muted-ink); }
.apx-prop-history summary { cursor: pointer; }
.apx-prop-history ol { margin: 6px 0 0; padding-left: 18px; display: grid; gap: 3px; }

.apx-tools { display: flex; gap: 2px; margin-top: 8px; }
.apx-tool {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--apx-muted-ink);
  cursor: pointer;
}
.apx-tool:hover { background: var(--apx-fill); color: var(--apx-foreground); }
.apx-tool.apx-on { color: var(--apx-brand); }
.apx-tool:focus-visible { outline: 2px solid var(--apx-brand); }
/* Màn chọn lý do 👎 (Story 5.6) */
.apx-fb { margin-top: 8px; padding: 10px; border: 1px solid var(--apx-border); border-radius: 10px; background: var(--apx-fill); display: grid; gap: 8px; }
.apx-fb-title { font-weight: 600; font-size: 13px; }
.apx-fb-cats { display: flex; flex-wrap: wrap; gap: 6px; }
.apx-fb-cat { border: 1px solid var(--apx-border); border-radius: 999px; padding: 4px 10px; background: transparent; color: inherit; font: inherit; font-size: 12px; cursor: pointer; }
.apx-fb-cat[aria-checked="true"] { border-color: var(--apx-brand); color: var(--apx-brand); font-weight: 600; }
.apx-fb-cat:focus-visible, .apx-fb-btn:focus-visible, .apx-fb-note:focus-visible { outline: 2px solid var(--apx-brand); }
.apx-fb-note { width: 100%; box-sizing: border-box; resize: vertical; border: 1px solid var(--apx-border); border-radius: 8px; padding: 6px 8px; font: inherit; font-size: 12px; background: var(--apx-card); color: inherit; }
.apx-fb-notice { margin: 0; font-size: 11px; color: var(--apx-muted-ink); }
.apx-fb-row { display: flex; justify-content: flex-end; gap: 6px; }
.apx-fb-btn { border: 1px solid var(--apx-border); border-radius: 8px; padding: 4px 12px; background: transparent; color: inherit; font: inherit; font-size: 12px; cursor: pointer; }
.apx-fb-send { background: var(--apx-brand); border-color: var(--apx-brand); color: var(--apx-brand-foreground); }
.apx-fb-send:disabled { opacity: .5; cursor: default; }

.apx-flash { outline: 2px solid var(--apx-brand); outline-offset: 3px; transition: outline-color 1.6s; }

/* =====================================================================
   Hub — chế độ toàn màn hình (chỉ khi có server)
   ===================================================================== */
.apx-frame { flex: 1; min-height: 0; display: flex; }
.apx-main { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; }
.apx-main[hidden], .apx-hubbar[hidden], .apx-side[hidden], .apx-aside[hidden], .apx-hubview[hidden],
.apx-convhead[hidden], .apx-toast[hidden] { display: none !important; }

.apx-root[data-hub="on"] .apx-head { display: none; }
.apx-root[data-hub="on"] .apx-panel { overflow: hidden; }

.apx-hubbar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  height: 54px;
  flex-shrink: 0;
  padding: 0 12px 0 16px;
  border-bottom: 1px solid var(--apx-border);
  background: var(--apx-card);
}
.apx-hub-brand { display: flex; align-items: center; gap: 7px; font-weight: 700; font-size: 15px; color: var(--apx-foreground); }
.apx-hub-logo { width: 28px; height: 28px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; color: #fff; background: var(--apx-brand); }
.apx-hub-tag { font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--apx-brand); border: 1px solid currentColor; border-radius: 99px; padding: 1px 6px; }
.apx-hub-tabs { display: flex; gap: 4px; margin-left: 10px; height: 100%; }
.apx-hub-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 0 10px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: none;
  color: var(--apx-ink2);
  font: 500 13.5px var(--apx-font);
  cursor: pointer;
}
.apx-hub-tab:hover { color: var(--apx-foreground); }
.apx-hub-tab.apx-on { color: var(--apx-foreground); font-weight: 650; border-bottom-color: var(--apx-brand); }
.apx-count { min-width: 18px; height: 18px; padding: 0 5px; border-radius: 99px; background: var(--apx-critical); color: #fff; font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
.apx-hub-spacer { flex: 1; }
.apx-hub-icon { position: relative; width: 34px; height: 34px; border: 0; border-radius: 9px; background: none; color: var(--apx-ink2); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.apx-hub-icon:hover { background: var(--apx-elev); color: var(--apx-foreground); }
.apx-dot { position: absolute; top: 3px; right: 2px; min-width: 16px; height: 16px; padding: 0 4px; border-radius: 99px; background: var(--apx-critical); color: #fff; font-size: 10px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
.apx-hub-who { display: flex; align-items: center; gap: 8px; padding: 0 6px; line-height: 1.2; }
.apx-hub-avatar { width: 30px; height: 30px; border-radius: 99px; background: var(--apx-elev); border: 1px solid var(--apx-border); display: inline-flex; align-items: center; justify-content: center; font-size: 11.5px; font-weight: 700; color: var(--apx-foreground); }
.apx-hub-whoname { font-size: 12.5px; font-weight: 650; color: var(--apx-foreground); }
.apx-hub-whorole { font-size: 11px; color: var(--apx-muted-ink); }

.apx-bellpop {
  position: absolute;
  top: 50px;
  right: 120px;
  z-index: 10;
  width: 360px;
  max-height: min(70vh, 520px);
  overflow-y: auto;
  padding: 8px;
  border: 1px solid var(--apx-border);
  border-radius: 12px;
  background: var(--apx-card);
  box-shadow: 0 18px 40px -12px rgba(0, 0, 0, 0.35);
}
.apx-bellhead { display: flex; align-items: center; justify-content: space-between; padding: 4px 6px 8px; font-size: 13px; color: var(--apx-foreground); }
.apx-notif { display: flex; gap: 10px; width: 100%; text-align: left; padding: 9px 8px; border: 0; border-radius: 9px; background: none; cursor: pointer; font-family: var(--apx-font); }
.apx-notif:hover { background: var(--apx-elev); }
.apx-unread { background: var(--apx-info-soft); }
.apx-notif-ico { width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; color: var(--apx-ink2); background: var(--apx-elev); }
.apx-k-approval { color: var(--apx-notice); background: var(--apx-notice-soft); }
.apx-k-handoff { color: var(--apx-brand); background: var(--apx-info-soft, #e8f1fc); }
.apx-k-done, .apx-k-job { color: var(--apx-success); background: var(--apx-success-soft); }
.apx-k-rejected, .apx-k-job_failed { color: var(--apx-critical); background: var(--apx-critical-soft); }
.apx-notif-body { display: grid; gap: 2px; min-width: 0; }
.apx-notif-title { font-size: 12.5px; font-weight: 650; color: var(--apx-foreground); }
.apx-notif-text { font-size: 12px; color: var(--apx-ink2); overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.apx-notif-time { font-size: 11px; color: var(--apx-muted-ink); }

/* cột trái */
.apx-side { width: 268px; flex-shrink: 0; display: flex; flex-direction: column; gap: 10px; padding: 12px; border-right: 1px solid var(--apx-border); background: var(--apx-elev); min-height: 0; }
.apx-new { width: 100%; min-height: 36px; }
.apx-searchwrap { position: relative; display: block; }
.apx-searchico { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--apx-muted-ink); display: inline-flex; }
.apx-search, .apx-rename, .apx-field, .apx-select, .apx-note {
  width: 100%;
  box-sizing: border-box;
  padding: 7px 10px;
  border: 1px solid var(--apx-border);
  border-radius: 9px;
  background: var(--apx-card);
  color: var(--apx-foreground);
  font: 13px var(--apx-font);
}
.apx-search { padding-left: 30px; }
.apx-search:focus, .apx-rename:focus, .apx-field:focus, .apx-select:focus, .apx-note:focus { outline: 2px solid var(--apx-brand); outline-offset: -1px; }
.apx-convlist { flex: 1; min-height: 0; overflow-y: auto; margin: 0 -6px; padding: 0 6px; scrollbar-width: thin; }
.apx-group { margin: 12px 4px 4px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--apx-muted-ink); }
.apx-conv { position: relative; display: flex; align-items: center; border-radius: 9px; }
.apx-conv:hover, .apx-conv.apx-on { background: var(--apx-card); }
.apx-conv.apx-on { box-shadow: inset 2px 0 0 var(--apx-brand); }
.apx-conv-open, .apx-hit { flex: 1; min-width: 0; display: grid; gap: 1px; text-align: left; padding: 7px 8px; border: 0; background: none; cursor: pointer; font-family: var(--apx-font); border-radius: 9px; }
.apx-hit:hover { background: var(--apx-card); }
.apx-conv-title { font-size: 12.8px; font-weight: 550; color: var(--apx-foreground); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.apx-conv-meta { font-size: 11px; color: var(--apx-muted-ink); }
.apx-hit-snippet { font-size: 11.5px; color: var(--apx-ink2); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.apx-conv-tools { display: none; gap: 1px; padding-right: 4px; }
.apx-conv:hover .apx-conv-tools, .apx-conv:focus-within .apx-conv-tools { display: flex; }
.apx-mini { width: 24px; height: 24px; border: 0; border-radius: 6px; background: none; color: var(--apx-muted-ink); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.apx-mini:hover { background: var(--apx-fill); color: var(--apx-foreground); }
.apx-mini.apx-on { color: var(--apx-brand); }
.apx-conv.apx-confirm { flex-wrap: wrap; }
.apx-ask { display: flex; gap: 10px; align-items: center; padding: 0 8px 8px; font-size: 12px; color: var(--apx-ink2); width: 100%; }

/* đầu khung chat */
.apx-convhead { display: flex; align-items: center; gap: 8px; padding: 9px 18px; border-bottom: 1px solid var(--apx-border); flex-shrink: 0; }
.apx-convhead-title { flex: 1; min-width: 0; font-weight: 650; font-size: 14px; color: var(--apx-foreground); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* cột phải */
.apx-aside { width: 304px; flex-shrink: 0; display: flex; flex-direction: column; border-left: 1px solid var(--apx-border); min-height: 0; }
.apx-aside-tabs { display: flex; gap: 2px; padding: 0 10px; border-bottom: 1px solid var(--apx-border); flex-shrink: 0; }
.apx-aside-tab { padding: 12px 8px 10px; border: 0; border-bottom: 2px solid transparent; background: none; color: var(--apx-ink2); font: 500 12.5px var(--apx-font); cursor: pointer; }
.apx-aside-tab.apx-on { color: var(--apx-foreground); font-weight: 650; border-bottom-color: var(--apx-brand); }
.apx-aside-body { flex: 1; min-height: 0; overflow-y: auto; padding: 12px; display: grid; align-content: start; gap: 9px; scrollbar-width: thin; }
.apx-aside-hint { margin: 0; font-size: 11.5px; color: var(--apx-muted-ink); line-height: 1.45; }
.apx-aside-foot { margin: 6px 0 0; font-size: 11px; color: var(--apx-muted-ink); }
.apx-empty { margin: 0; padding: 26px 8px; text-align: center; font-size: 12.5px; color: var(--apx-muted-ink); line-height: 1.5; }
.apx-loading { margin: 0; padding: 30px; text-align: center; font-size: 13px; color: var(--apx-muted-ink); }
.apx-error-line { margin: 0; color: var(--apx-critical); }
.apx-srccard { border: 1px solid var(--apx-border); border-radius: 10px; padding: 9px 11px; display: grid; gap: 3px; background: var(--apx-card); }
.apx-srchead { display: flex; gap: 8px; align-items: flex-start; }
.apx-srcnum { width: 18px; height: 18px; border-radius: 5px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; font-size: 10.5px; font-weight: 700; color: var(--apx-info); background: var(--apx-info-soft); }
.apx-srclabel { font-size: 12.5px; font-weight: 600; color: var(--apx-foreground); }
.apx-srcmeta, .apx-srcnote { font-size: 11.5px; color: var(--apx-muted-ink); }
.apx-srcnote { font-style: italic; }
.apx-act-block { border: 1px solid var(--apx-border); border-radius: 10px; padding: 9px 11px; background: var(--apx-card); }
.apx-act-q { display: block; width: 100%; text-align: left; border: 0; background: none; padding: 0 0 6px; font: 600 12.5px var(--apx-font); color: var(--apx-foreground); cursor: pointer; }
.apx-steps { margin: 0; padding: 0; list-style: none; display: grid; gap: 4px; }
.apx-step { display: grid; grid-template-columns: 44px 1fr; gap: 6px; font-size: 11.8px; color: var(--apx-ink2); padding-left: 6px; border-left: 2px solid var(--apx-success); }
.apx-step.apx-k-web { border-left-color: var(--apx-info); }
.apx-step.apx-k-write { border-left-color: var(--apx-notice); }
.apx-step-t { font-family: var(--apx-mono); font-size: 10.5px; color: var(--apx-muted-ink); }
.apx-artrow { display: flex; align-items: center; gap: 9px; width: 100%; padding: 9px 10px; border: 1px solid var(--apx-border); border-radius: 10px; background: var(--apx-card); color: var(--apx-foreground); text-decoration: none; font: 12.5px var(--apx-font); cursor: pointer; text-align: left; }
.apx-artrow:hover { border-color: var(--apx-brand); }
.apx-artico { color: var(--apx-brand); display: inline-flex; }
.apx-arttitle { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* vùng các tab khác */
.apx-hubview { flex: 1; min-width: 0; min-height: 0; overflow-y: auto; padding: 18px 22px; background: var(--apx-card); }
.apx-md { display: grid; grid-template-columns: 340px minmax(0, 1fr); gap: 20px; align-items: start; max-width: 1280px; margin: 0 auto; }
.apx-md-list { display: grid; gap: 8px; position: sticky; top: 0; }
.apx-md-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.apx-md-title { margin: 0; font-size: 17px; font-weight: 700; color: var(--apx-foreground); }
.apx-md-detail { min-width: 0; display: grid; gap: 12px; }
.apx-seg { display: flex; flex-wrap: wrap; gap: 4px; padding: 3px; border-radius: 10px; background: var(--apx-elev); }
.apx-seg-btn { flex: 1; padding: 6px 8px; border: 0; border-radius: 8px; background: none; color: var(--apx-ink2); font: 600 12px var(--apx-font); cursor: pointer; white-space: nowrap; }
.apx-seg-btn.apx-on { background: var(--apx-card); color: var(--apx-foreground); box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08); }
.apx-item { display: grid; gap: 3px; width: 100%; text-align: left; padding: 10px 12px; border: 1px solid var(--apx-border); border-radius: 11px; background: var(--apx-card); cursor: pointer; font-family: var(--apx-font); }
.apx-item:hover { border-color: var(--apx-ink2); }
.apx-item.apx-on { border-color: var(--apx-brand); box-shadow: 0 0 0 1px var(--apx-brand); }
.apx-item-top { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.apx-item-time { font-size: 11px; color: var(--apx-muted-ink); }
.apx-item-title { font-size: 13px; font-weight: 650; color: var(--apx-foreground); }
.apx-item-sub { font-size: 11.5px; color: var(--apx-muted-ink); }
.apx-item-state { justify-self: start; font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 99px; }
.apx-badge { font-size: 10.5px; font-weight: 700; padding: 2px 7px; border-radius: 99px; color: var(--apx-ink2); background: var(--apx-elev); }
.apx-pri-high { color: var(--apx-critical); background: var(--apx-critical-soft); }
.apx-pri-sched { color: var(--apx-info); background: var(--apx-info-soft); }
.apx-pri-paused { color: var(--apx-notice); background: var(--apx-notice-soft); }
.apx-decide { display: grid; gap: 4px; }
.apx-note { resize: vertical; }
.apx-jobform { display: grid; gap: 6px; max-width: 640px; }
.apx-lbl { font-size: 12px; font-weight: 600; color: var(--apx-ink2); margin-top: 6px; }
.apx-sched { display: flex; gap: 6px; flex-wrap: wrap; }
.apx-sched .apx-select { width: auto; }
.apx-jobhead { display: grid; gap: 6px; }
.apx-jobprompt { margin: 0; font-size: 12.5px; color: var(--apx-ink2); }
.apx-jobctl { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.apx-versions { width: auto; max-width: 100%; justify-self: start; }
.apx-report { max-width: 100% !important; }
.apx-overview { display: grid; gap: 10px; max-width: 1080px; margin: 0 auto; }

.apx-toast { position: absolute; left: 50%; bottom: 24px; transform: translateX(-50%); z-index: 20; padding: 9px 14px; border-radius: 10px; background: #111827; color: #fff; font: 600 12.5px var(--apx-font); box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5); max-width: 80%; }

@media (max-width: 1180px) {
  .apx-root[data-hub="on"] .apx-aside { display: none !important; }
}
@media (max-width: 900px) {
  .apx-side { width: 220px; }
  .apx-md { grid-template-columns: 1fr; }
  .apx-md-list { position: static; }
  .apx-hub-who > div { display: none; }
}

/* kiểm tra trước khi ghi */
.apx-checks { margin-top: 9px; padding: 8px 10px; border-radius: 8px; border: 1px solid var(--apx-border); display: grid; gap: 5px; }
.apx-checks-title { font-size: 11px; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase; color: var(--apx-muted-ink); }
.apx-check { display: flex; gap: 7px; align-items: flex-start; font-size: 12px; line-height: 1.45; color: var(--apx-ink2); }
.apx-check b { color: var(--apx-foreground); font-weight: 600; }
.apx-check-ico { width: 16px; height: 16px; border-radius: 99px; flex-shrink: 0; margin-top: 1px; display: inline-flex; align-items: center; justify-content: center; }
.apx-check-ok .apx-check-ico { color: var(--apx-success); background: var(--apx-success-soft); }
.apx-check-warn .apx-check-ico { color: var(--apx-notice); background: var(--apx-notice-soft); }
.apx-check-block .apx-check-ico { color: var(--apx-critical); background: var(--apx-critical-soft); }
.apx-prop-override { margin-top: 9px; }
.apx-need { outline: 2px solid var(--apx-critical) !important; }
.apx-prop-blocked { border-color: var(--apx-critical); }

/* ---------- các bước agent (gập), chip ý định, tệp đính kèm ---------- */
.apx-run { margin: 0 0 8px; font-size: 11.5px; color: var(--apx-muted-ink); }
.apx-run summary { cursor: pointer; user-select: none; }
.apx-run .apx-steps { margin-top: 6px; }
.apx-intent { display: inline-flex; align-items: center; margin-right: 6px; padding: 2px 8px; border-radius: 99px; font-size: 10.5px; font-weight: 650; color: var(--apx-info); background: var(--apx-info-soft); white-space: nowrap; }
.apx-in-act { color: var(--apx-notice); background: var(--apx-notice-soft); }
.apx-in-research { color: #7c3aed; background: rgba(124, 58, 237, 0.1); }
.apx-in-analyze, .apx-in-present { color: var(--apx-success); background: var(--apx-success-soft); }
.apx-in-guard { color: var(--apx-muted-ink); background: var(--apx-elev); }
.apx-tools { align-items: center; }
.apx-attach { color: var(--apx-muted-ink); }
.apx-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }
.apx-attached { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.apx-filechip { display: inline-flex; align-items: center; gap: 4px; max-width: 260px; padding: 3px 4px 3px 8px; border-radius: 8px; font-size: 11.5px; color: var(--apx-ink2); background: var(--apx-elev); border: 1px solid var(--apx-border); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.apx-msg-user .apx-filechip { color: var(--apx-brand-foreground); background: rgba(255, 255, 255, 0.16); border-color: rgba(255, 255, 255, 0.3); padding-right: 8px; }
.apx-fc-uploading { opacity: 0.7; }
.apx-fc-error { color: var(--apx-critical); border-color: var(--apx-critical); }
.apx-unattach { width: 18px; height: 18px; border: 0; border-radius: 5px; background: none; color: inherit; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.apx-unattach:hover { background: var(--apx-fill); }

/* ---------- chia sẻ, ghi nhớ, giao việc ---------- */
.apx-convhead { position: relative; }
.apx-sharepop { position: absolute; right: 12px; top: 44px; z-index: 10; width: 380px; padding: 10px; border: 1px solid var(--apx-border); border-radius: 12px; background: var(--apx-card); box-shadow: 0 18px 40px -12px rgba(0, 0, 0, 0.35); display: grid; gap: 8px; }
.apx-shareitem { display: flex; gap: 10px; align-items: center; font-size: 12px; padding-top: 6px; border-top: 1px solid var(--apx-border); }
.apx-shareitem .apx-a { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.apx-memhead { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 6px; font-size: 13px; color: var(--apx-foreground); }
.apx-switch { padding: 3px 10px; border-radius: 99px; border: 1px solid var(--apx-border); background: var(--apx-elev); color: var(--apx-muted-ink); font: 600 11px var(--apx-font); cursor: pointer; }
.apx-switch.apx-on { color: #fff; background: var(--apx-success); border-color: var(--apx-success); }
.apx-memrow { display: grid; grid-template-columns: 1fr auto; gap: 2px 6px; align-items: center; padding: 8px 10px; border: 1px solid var(--apx-border); border-radius: 10px; background: var(--apx-card); }
.apx-memtext { grid-column: 1; font-size: 12.5px; color: var(--apx-foreground); }
.apx-memrow .apx-conv-meta { grid-column: 1; }
.apx-memrow .apx-mini { grid-column: 2; grid-row: 1 / span 2; }
.apx-memadd { display: flex; gap: 6px; }
.apx-memadd .apx-search { padding-left: 10px; }
.apx-ideas { display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
.apx-ideas .apx-chip { text-align: left; white-space: normal; }
.apx-taskprops { display: grid; gap: 4px; }

/* =====================================================================
   Linh vật Apus Ami, đổi vai (demo), thẻ số liệu quản trị
   ===================================================================== */
.apx-launcher.apx-has-mascot { background: #fff; border: 3px solid var(--apx-brand); overflow: hidden; }
.apx-launcher-img { width: 100%; height: 100%; object-fit: cover; object-position: 50% 30%; border-radius: 50%; }
.apx-launcher.apx-has-mascot svg { display: none; }
.apx-root[data-open="true"] .apx-launcher.apx-has-mascot { background: var(--apx-grad); }
.apx-root[data-open="true"] .apx-launcher.apx-has-mascot .apx-launcher-img { display: none; }
.apx-root[data-open="true"] .apx-launcher.apx-has-mascot svg { display: block; color: #fff; }
.apx-avatar-img { width: 100%; height: 100%; object-fit: cover; object-position: 50% 30%; border-radius: inherit; background: #fff; }
.apx-hub-logo.apx-has-mascot { background: #fff; border: 2px solid var(--apx-brand); overflow: hidden; }
.apx-hub-logo-img { width: 100%; height: 100%; object-fit: cover; object-position: 50% 30%; }
.apx-hub-tag { color: var(--apx-accent); }

/* Linh vật Ami có mắt chớp (src/ui/mascot.ts) — ba lớp trùng khung; cắt cận đầu như nút thu gọn của web-marketing. */
.apx-ami { position: relative; display: block; overflow: hidden; background: #fff; }
.apx-ami-layer { position: absolute; inset: 0; width: 100%; height: 100%; max-width: none; margin: 0; object-fit: cover; pointer-events: none; user-select: none; }
.apx-ami-head .apx-ami-layer { transform: scale(2.75); transform-origin: 55% 28%; }
.apx-ami-full .apx-ami-layer { object-fit: contain; }
.apx-ami-full .apx-ami-wing { transform-origin: 40% 40%; animation: apx-ami-wing 8s cubic-bezier(0.22, 0.78, 0.24, 1) infinite; }
.apx-ami-eyes { opacity: 0; transition: opacity 35ms linear; }
.apx-ami[data-blinking="true"] .apx-ami-eyes { opacity: 1; }
/* Nút nổi: Ami "thở" nhẹ khi chờ. Đang trả lời: đầu nghiêng qua lại như đang nghĩ. */
.apx-launcher .apx-ami-head .apx-ami-layer { animation: apx-ami-breathe 4.8s ease-in-out infinite; }
.apx-root[data-ami-thinking] .apx-avatar .apx-ami-head .apx-ami-layer,
.apx-root[data-ami-thinking] .apx-hub-logo .apx-ami-head .apx-ami-layer { animation: apx-ami-think 1.4s ease-in-out infinite; }
@keyframes apx-ami-breathe { 0%, 100% { transform: scale(2.75) translateY(0); } 50% { transform: scale(2.79) translateY(-0.7%); } }
@keyframes apx-ami-think { 0%, 100% { transform: scale(2.75) rotate(0deg); } 30% { transform: scale(2.75) rotate(-6deg); } 70% { transform: scale(2.75) rotate(5deg); } }
@keyframes apx-ami-wing { 0%, 70%, 100% { transform: rotate(0deg); } 74% { transform: rotate(-2.8deg); } 78% { transform: rotate(1.4deg); } 82% { transform: rotate(-1.8deg); } 86% { transform: rotate(0deg); } }
@media (prefers-reduced-motion: reduce) {
  .apx-ami .apx-ami-layer { animation: none !important; }
}

.apx-rolebtn { display: inline-flex; align-items: center; gap: 7px; margin-left: auto; max-width: 210px; padding: 3px 8px 3px 3px; border: 1px solid var(--apx-border); border-radius: 99px; background: var(--apx-card); color: var(--apx-foreground); cursor: pointer; font-family: var(--apx-font); }
.apx-rolebtn:hover { border-color: var(--apx-brand); }
.apx-rolebtn[hidden] { display: none; }
.apx-roleav { width: 26px; height: 26px; border-radius: 99px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; font-size: 10.5px; font-weight: 700; color: #fff; background: var(--apx-grad); }
.apx-roletx { display: grid; min-width: 0; text-align: left; line-height: 1.15; }
.apx-roletx b { font-size: 12px; font-weight: 650; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.apx-roletx small { font-size: 10.5px; color: var(--apx-muted-ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.apx-caret { font-size: 10px; color: var(--apx-muted-ink); }
.apx-rolemenu { position: absolute; z-index: 30; top: 56px; right: 12px; width: 270px; padding: 6px; border: 1px solid var(--apx-border); border-radius: 12px; background: var(--apx-card); box-shadow: 0 18px 40px -12px rgba(0, 0, 0, 0.35); display: grid; gap: 2px; }
.apx-rolemenu[hidden] { display: none; }
.apx-rolemenu-h { padding: 6px 8px 4px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--apx-muted-ink); }
.apx-roleitem { display: flex; align-items: center; gap: 9px; width: 100%; padding: 7px 8px; border: 0; border-radius: 9px; background: none; color: var(--apx-foreground); cursor: pointer; font-family: var(--apx-font); }
.apx-roleitem:hover, .apx-roleitem:focus-visible { background: var(--apx-elev); outline: none; }
.apx-roleitem.apx-on { background: var(--apx-info-soft); }
.apx-root[data-hub="on"] .apx-rolebtn { display: none; }
.apx-hub-whobtn { border: 1px solid transparent; border-radius: 99px; background: none; cursor: pointer; font-family: var(--apx-font); padding: 3px 10px 3px 3px; }
.apx-hub-whobtn:hover { border-color: var(--apx-border); background: var(--apx-elev); }
.apx-hub-whotext { text-align: left; }
.apx-hub-avatar { background: var(--apx-grad); color: #fff; border: 0; }
.apx-hub-tab.apx-on { border-bottom-color: var(--apx-accent); }

.apx-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
.apx-kpi { display: grid; gap: 2px; padding: 12px 14px; border: 1px solid var(--apx-border); border-radius: 12px; background: var(--apx-card); border-top: 3px solid var(--apx-brand); }
.apx-kpi:nth-child(2) { border-top-color: var(--apx-accent); }
.apx-kpi-l { font-size: 11.5px; color: var(--apx-muted-ink); font-weight: 600; }
.apx-kpi-v { font-size: 22px; font-weight: 700; color: var(--apx-foreground); font-variant-numeric: tabular-nums; }
.apx-kpi-s { font-size: 11px; color: var(--apx-muted-ink); }

/* gợi ý hỏi tiếp dưới câu trả lời mới nhất */
.apx-followups { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin-top: 10px; }
.apx-followups-l { font-size: 11px; font-weight: 700; color: var(--apx-muted-ink); text-transform: uppercase; letter-spacing: 0.04em; margin-right: 2px; }
.apx-followup { text-align: left; white-space: normal; border-color: color-mix(in srgb, var(--apx-brand) 35%, var(--apx-border)); }
.apx-followup:hover { border-color: var(--apx-brand); color: var(--apx-brand); }
.apx-thread > .apx-msg:not(:last-child) .apx-followups { display: none; }

/* ==========================================================================
   Apus Ami ĐỨNG + BAY VÀO (src/ui/pet.ts) — chép kích thước/nhịp mascot web-marketing (apus-ami-widget/src/ami.css
   D-15/D-17): vỏ 200×235 neo góc phải dưới, sân khấu 200 px; cổng 158 px ở góc trái dưới.
   `data-pet` trên .apx-root: entrance | standing | compact — chỉ `compact` mới hiện nút tròn.
   ========================================================================== */
.apx-root[data-pet="entrance"] .apx-launcher,
.apx-root[data-pet="standing"] .apx-launcher { visibility: hidden; pointer-events: none; }
.apx-root[data-pet-anim] .apx-launcher { animation: apx-compact-in 260ms cubic-bezier(0.2, 0.8, 0.2, 1) backwards; }
@keyframes apx-compact-in { from { opacity: 0; transform: scale(0.6); } }

.apx-petshell {
  --ami-accent: #d9b13b; --ami-accent-hi: #eed9a3; --ami-sky: #58b1e4; --ami-brand: #1565d8;
  position: fixed; right: 16px; bottom: 12px; z-index: 4;
  width: 200px; height: 235px; display: none; pointer-events: none;
}
.apx-root[data-pet="standing"] .apx-petshell { display: block; animation: apx-pet-in 440ms ease-out both; }
.apx-root[data-open="true"] .apx-petshell { display: none; }
@keyframes apx-pet-in { from { opacity: 0; } }
.apx-pet {
  position: absolute; inset: 32px 0 0; display: block; width: 100%; height: 203px; padding: 0; border: 0;
  background: transparent; cursor: pointer; pointer-events: auto; -webkit-tap-highlight-color: transparent;
}
.apx-pet:focus-visible { outline: none; }
.apx-pet:focus-visible .apx-pet-cue { box-shadow: 0 0 0 3px rgba(245, 128, 32, 0.28), 0 14px 32px -15px rgba(4, 9, 20, 0.88); }
.apx-pet-cue {
  position: absolute; right: 0; bottom: calc(100% - 41px); z-index: 8; display: grid; gap: 2px;
  width: min(230px, calc(100vw - 32px)); min-width: 142px; padding: 8px 12px 7px;
  border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 14px 14px 4px;
  background: rgba(10, 24, 56, 0.92); color: #fff; text-align: left;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.14), 0 14px 32px -15px rgba(4, 9, 20, 0.88);
  -webkit-backdrop-filter: blur(18px) saturate(145%); backdrop-filter: blur(18px) saturate(145%);
  transform-origin: bottom right;
}
.apx-pet-cue::after {
  content: ""; position: absolute; right: 20px; bottom: -7px; width: 13px; height: 13px;
  border-right: 1px solid rgba(255, 255, 255, 0.2); border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(10, 24, 56, 0.92); transform: rotate(45deg);
}
.apx-pet-cue-title { font-size: 11px; line-height: 1.2; font-weight: 700; }
.apx-pet-cue-status { position: relative; color: rgba(255, 255, 255, 0.78); font-size: 10.5px; line-height: 1.35; }
.apx-pet-cue-ghost { visibility: hidden; }
.apx-pet-cue-typed { position: absolute; inset: 0; }
.apx-pet-stage {
  position: absolute; left: 0; right: 0; bottom: 0; height: 200px; transform-origin: center bottom;
  filter: drop-shadow(0 14px 14px rgba(4, 9, 20, 0.38)); animation: apx-pet-breathe 4.8s ease-in-out infinite;
}
.apx-pet-rig, .apx-pet-layer { position: absolute; inset: 0; width: 100%; height: 100%; max-width: none; margin: 0; }
.apx-pet-layer { object-fit: contain; pointer-events: none; user-select: none; }
.apx-pet-wing { transform-origin: 40% 40%; animation: apx-ami-wing 8s cubic-bezier(0.22, 0.78, 0.24, 1) infinite; }
.apx-pet-eyes { opacity: 0; transition: opacity 35ms linear; }
.apx-pet-rig[data-blinking="true"] .apx-pet-eyes { opacity: 1; }
.apx-pet-ground {
  position: absolute; left: 24%; bottom: 7%; z-index: -1; width: 55%; height: 8%;
  border-radius: 50%; background: rgba(4, 9, 20, 0.28); filter: blur(7px);
}
.apx-pet:hover .apx-pet-stage { animation-play-state: paused; transform: translateY(-2px); }
@keyframes apx-pet-breathe { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-2px) scale(1.008); } }
/* nút "–" thu gọn: hiện khi rê chuột / focus (bản React `.mas-compact-control`) */
.apx-pet-fold {
  position: absolute; left: 6px; bottom: 18px; z-index: 9; width: 26px; height: 26px; padding: 0;
  display: grid; place-items: center; border: 1px solid rgba(255, 255, 255, 0.5); border-radius: 50%;
  background: rgba(10, 24, 56, 0.86); color: #fff; font: 700 15px/1 system-ui, sans-serif;
  cursor: pointer; pointer-events: auto; opacity: 0; transition: opacity 160ms ease;
}
.apx-petshell:hover .apx-pet-fold, .apx-petshell:focus-within .apx-pet-fold { opacity: 1; }
.apx-pet-fold:focus-visible { outline: 3px solid var(--ami-brand); outline-offset: 2px; }

/* ---------- màn VÀO ---------- */
.apx-entrance {
  --ami-bottom: 12px; position: fixed; inset: 0; z-index: 3; overflow: hidden; pointer-events: none;
  visibility: hidden;
}
.apx-entrance[data-phase] { visibility: visible; }
.apx-entrance-stage { position: absolute; inset: 0; overflow: visible; }
.apx-entrance-fill { position: absolute; inset: 0; width: 100%; height: 100%; max-width: none; margin: 0; object-fit: contain; }
.apx-entrance-portal {
  position: absolute; left: max(8px, 1.2vw); bottom: calc(var(--ami-bottom) - 40.38px); width: 158px; aspect-ratio: 2 / 3;
  opacity: 0; filter: drop-shadow(0 18px 18px rgba(4, 9, 20, 0.28)); transform: scale(0.9, 0.72); transform-origin: center 82.963%;
}
.apx-entrance-wordmark {
  position: absolute; left: max(32px, calc(max(32px, 1.2vw) + (158px - 286px) / 2)); bottom: calc(var(--ami-bottom) + 180px);
  width: 286px; height: 60px; opacity: 0; transform-origin: center bottom;
}
.apx-entrance-aura {
  position: absolute; left: 18px; bottom: calc(var(--ami-bottom) + 8px); width: 138px; height: 220px; border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(238, 217, 163, 0.32), transparent 68%); opacity: 0; filter: blur(9px); transform: scale(0.72, 0.9);
}
.apx-entrance-trail { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; opacity: 0; z-index: 2; }
.apx-entrance-trail-glow, .apx-entrance-trail-core { fill: none; stroke-linecap: round; vector-effect: non-scaling-stroke; }
.apx-entrance-trail-glow { stroke: #d9b13b; stroke-width: 13px; opacity: 0.34; filter: blur(5px); }
.apx-entrance-trail-core { stroke-width: 2.5px; filter: drop-shadow(0 0 4px rgba(238, 217, 163, 0.72)); }
.apx-entrance-flyer {
  position: absolute; top: 0; left: 0; z-index: 3; width: 200px; height: 200px; opacity: 0; transform-origin: center;
  filter: drop-shadow(0 13px 12px rgba(4, 9, 20, 0.22)); will-change: opacity, transform;
}
.apx-entrance-frame { opacity: 0; }
.apx-entrance-frame.apx-on { opacity: 1; }
.apx-entrance-landed {
  position: absolute; right: 16px; bottom: var(--ami-bottom); z-index: 4; width: 200px; height: 200px; opacity: 0;
  transform-origin: center bottom; filter: drop-shadow(0 14px 14px rgba(4, 9, 20, 0.38));
}
.apx-entrance-shadow { position: absolute; left: 24%; right: 24%; bottom: 7%; z-index: -1; height: 8%; border-radius: 50%; background: rgba(4, 9, 20, 0.28); filter: blur(6px); }
.apx-entrance-ripple {
  position: absolute; right: 27px; bottom: calc(var(--ami-bottom) + 11px); width: 178px; height: 18px; z-index: 2;
  border: 1px solid rgba(238, 217, 163, 0.42); border-radius: 50%; opacity: 0; transform: scaleX(0.42);
}
.apx-entrance[data-phase="portal-forming"] .apx-entrance-portal { animation: apx-portal-form 1.5s cubic-bezier(0.2, 0.78, 0.2, 1) both; }
.apx-entrance[data-phase="portal-forming"] .apx-entrance-wordmark { animation: apx-wordmark-in 840ms 440ms cubic-bezier(0.2, 0.78, 0.2, 1) both; }
.apx-entrance[data-phase="portal-forming"] .apx-entrance-aura { animation: apx-aura-form 1.5s ease-out both; }
.apx-entrance[data-phase="portal-opening"] .apx-entrance-portal { opacity: 1; transform: none; animation: apx-portal-open 700ms cubic-bezier(0.2, 0.78, 0.2, 1) both; }
.apx-entrance[data-phase="portal-opening"] .apx-entrance-aura { animation: apx-aura-open 700ms ease-in-out both; }
.apx-entrance[data-phase="portal-opening"] .apx-entrance-wordmark,
.apx-entrance[data-phase="flight"] .apx-entrance-portal,
.apx-entrance[data-phase="flight"] .apx-entrance-wordmark { opacity: 1; transform: none; }
.apx-entrance[data-phase="flight"] .apx-entrance-aura { opacity: 0.68; transform: scale(1); }
.apx-entrance[data-phase="landing"] .apx-entrance-portal,
.apx-entrance[data-phase="landing"] .apx-entrance-wordmark { opacity: 1; transform: none; animation: apx-portal-withdraw 900ms 180ms cubic-bezier(0.4, 0, 0.2, 1) both; }
.apx-entrance[data-phase="landing"] .apx-entrance-aura { animation: apx-aura-withdraw 820ms 180ms ease-in both; }
.apx-entrance[data-phase="landing"] .apx-entrance-flyer { animation: apx-fade-out 220ms linear both; }
.apx-entrance[data-phase="landing"] .apx-entrance-landed { animation: apx-fade-in 220ms linear both; }
.apx-entrance[data-phase="landing"] .apx-entrance-ripple { animation: apx-ripple 760ms ease-out both; }
.apx-entrance[data-phase="crossfade"] .apx-entrance-portal,
.apx-entrance[data-phase="crossfade"] .apx-entrance-wordmark,
.apx-entrance[data-phase="crossfade"] .apx-entrance-flyer { opacity: 0; }
.apx-entrance[data-phase="crossfade"] .apx-entrance-landed { opacity: 1; filter: none; animation: apx-fade-out 440ms ease-out both; }
@keyframes apx-portal-form { 0% { opacity: 0; transform: scale(0.9, 0.72); } 42% { opacity: 0.74; } 100% { opacity: 1; transform: none; } }
@keyframes apx-wordmark-in { from { opacity: 0; transform: translateY(7px) scale(0.985); } to { opacity: 1; transform: none; } }
@keyframes apx-aura-form { 0% { opacity: 0; transform: scale(0.72, 0.9); } 100% { opacity: 0.68; transform: scale(1); } }
@keyframes apx-portal-open { 48% { transform: scale(1.016); } }
@keyframes apx-aura-open { 0%, 100% { opacity: 0.68; transform: scale(1); } 50% { opacity: 0.92; transform: scale(1.1, 1.04); } }
@keyframes apx-portal-withdraw { 0%, 25% { opacity: 1; transform: none; } 100% { opacity: 0; transform: translateY(-8px) scale(0.88, 0.7); } }
@keyframes apx-aura-withdraw { from { opacity: 0.7; transform: scale(1); } to { opacity: 0; transform: scale(0.76, 0.92); } }
@keyframes apx-fade-out { from { opacity: 1; } to { opacity: 0; } }
@keyframes apx-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes apx-ripple { 0% { opacity: 0; transform: scaleX(0.42); } 28% { opacity: 0.56; } 100% { opacity: 0; transform: scaleX(1.08); } }
@media (prefers-reduced-motion: reduce) {
  .apx-pet-stage, .apx-pet-wing { animation: none !important; }
  .apx-entrance { display: none; }
}

/* ---------- cỡ thu nhỏ: nút vai chỉ còn avatar + mũi tên — tên "Apus Ami" ⛔ bị che (tên/chức vụ ở title + menu) ---------- */
.apx-root[data-size="compact"] .apx-rolebtn { padding: 3px; gap: 3px; max-width: none; }
.apx-root[data-size="compact"] .apx-rolebtn .apx-roletx { display: none; }
.apx-headtext { min-width: 72px; }
.apx-rolemenu { max-width: calc(100% - 16px); }
.apx-roletx b, .apx-roletx small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.apx-rolemenu .apx-roletx { min-width: 0; }

/* ---------- xem trước tệp (src/ui/preview.ts) ---------- */
.apx-filechip-open { cursor: pointer; border: 1px solid var(--apx-border); font: inherit; color: inherit; }
.apx-filechip-open:hover { border-color: var(--apx-brand); color: var(--apx-brand); }
/* tệp Apus Ami vừa TẠO (Word/Excel/PowerPoint/ảnh): chip to hơn, viền thương hiệu — bấm để xem trước + tải về */
.apx-msg-assistant .apx-attached { margin-top: 8px; }
.apx-msg-assistant .apx-filechip-open { max-width: 340px; padding: 6px 10px; font-size: 12.5px; border-color: var(--apx-brand); color: var(--apx-brand); background: var(--apx-elev); }
.apx-preview { position: absolute; inset: 0; z-index: 40; display: flex; flex-direction: column; background: var(--apx-bg, var(--apx-card)); }
.apx-preview[hidden], .apx-preview-bar [hidden] { display: none; }
.apx-preview-bar { display: flex; align-items: center; gap: 4px; padding: 8px 8px 8px 14px; border-bottom: 1px solid var(--apx-border); background: var(--apx-card); }
.apx-preview-title { flex: 1; min-width: 0; font-weight: 650; font-size: 13.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--apx-foreground); }
.apx-preview-body { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; overflow: auto; background: var(--apx-elev); }
.apx-preview-frame { width: 100%; height: 100%; border: 0; background: #fff; }
.apx-preview-img { max-width: 100%; max-height: 100%; object-fit: contain; }
.apx-preview-video { max-width: 100%; max-height: 100%; background: #000; }
.apx-preview-msg { padding: 24px; text-align: center; color: var(--apx-muted-ink); font-size: 13.5px; }
.apx-preview-err { color: var(--apx-critical); }

/* thư chờ xác nhận: sửa người nhận / CC / tiêu đề ngay trên thẻ */
.apx-prop-edit {
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  font-weight: 500;
  color: var(--apx-foreground);
  background: var(--apx-surface, #fff);
  border: 1px solid var(--apx-border, rgba(0, 0, 0, 0.14));
  border-radius: 6px;
  padding: 3px 7px;
  min-width: 0;
}
.apx-prop-edit:focus { outline: 2px solid var(--apx-brand); outline-offset: 0; border-color: transparent; }
.apx-prop-suggest { padding: 2px 8px; font-size: 12px; margin-left: 2px; }

/* hộp nhập mật khẩu (Xoá dữ liệu demo) */
.apx-ask { position: absolute; inset: 0; z-index: 60; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(15, 26, 46, 0.45); }
.apx-ask-box { width: 100%; max-width: 380px; background: var(--apx-card); color: var(--apx-foreground); border-radius: 14px; padding: 18px 18px 14px; box-shadow: 0 18px 50px -12px rgba(4, 9, 20, 0.45); display: grid; gap: 10px; }
.apx-ask-title { font-weight: 700; font-size: 15px; }
.apx-ask-msg { margin: 0; font-size: 13px; line-height: 1.5; color: var(--apx-muted-ink); }
.apx-ask-input { font: inherit; font-size: 14px; padding: 9px 11px; border-radius: 9px; border: 1px solid var(--apx-border); background: var(--apx-bg, var(--apx-card)); color: var(--apx-foreground); }
.apx-ask-input:focus { outline: 2px solid var(--apx-brand); outline-offset: 0; border-color: transparent; }
.apx-ask-err { margin: 0; font-size: 12.5px; color: var(--apx-critical); }
.apx-ask-err[hidden] { display: none; }
.apx-ask-buttons { display: flex; justify-content: flex-end; gap: 8px; margin-top: 2px; }

/* Story 13.6 — "Tài khoản ERP" (dùng khung popover của chuông) */
.apx-erppop .apx-erprow { display: flex; align-items: center; gap: 10px; cursor: default; }
.apx-erppop .apx-erprow .apx-notif-body { flex: 1; min-width: 0; }
.apx-erppop .apx-erprow .apx-btn { flex: none; }
/* Story 12.10/13.13 — "Kênh nhận thông báo": mã liên kết một lần */
.apx-chnpop .apx-chncode { margin: 4px 12px 8px; padding: 8px 10px; border: 1px dashed var(--apx-border, #d0d7e2); border-radius: 8px; }
.apx-chnpop .apx-chncode-code { display: block; font: 600 18px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: 2px; user-select: all; }
